import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteConfig } from '../../../config/site';

interface ApplianceBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  service: {
    id: string;
    title: string;
    price: number;
    hasEncastreOption?: boolean;
  } | null;
}

const ApplianceBookingModal: React.FC<ApplianceBookingModalProps> = ({ isOpen, onClose, service }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    email: '',
    rua: '',
    localidade: '',
    distrito: '',
    codigoPostal: '',
    observacoes: '',
    quantidade: '1',
    remocaoDescarte: false,
    isEncastre: false,
    rgpdConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Reset status when opening
      setSubmitStatus('idle');
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isOpen]);

  if (!isOpen || !service) return null;

  const calculateTotal = () => {
    let base = service.price * parseInt(formData.quantidade);
    if (formData.isEncastre) {
      base += 30.00 * parseInt(formData.quantidade);
    }
    return base.toFixed(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.rgpdConsent) {
      alert('Por favor, aceite os termos de privacidade para continuar.');
      return;
    }

    setIsSubmitting(true);
    try {
      const summary = `*** ORIGEM: ALPHA SERVICES - INSTALAÇÃO ***\n\n` +
        `Serviço: ${service.title}\n` +
        `Quantidade: ${formData.quantidade}\n` +
        `Equipamento de Encastre: ${formData.isEncastre ? 'Sim (+30€/un)' : 'Não'}\n` +
        `Remoção e Descarte: ${formData.remocaoDescarte ? 'Sim' : 'Não'}\n` +
        `Valor Estimado: ${calculateTotal()}€\n\n` +
        `Dados do Cliente:\n` +
        `- Nome: ${formData.nome}\n` +
        `- Telefone: ${formData.telefone}\n` +
        `- Email: ${formData.email}\n\n` +
        `Endereço Detalhado:\n` +
        `- Rua: ${formData.rua}\n` +
        `- Localidade: ${formData.localidade}\n` +
        `- Distrito: ${formData.distrito}\n` +
        `- Cód. Postal: ${formData.codigoPostal}\n\n` +
        `Observações: ${formData.observacoes || 'Nenhuma'}\n\n` +
        `O cliente concorda com o pagamento de 50% para garantia da data e está ciente dos termos de instalação.`;

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'da198dbc-68bf-4341-b543-3c734419e8f0',
          subject: `NOVO PEDIDO: Instalação de ${service.title}`,
          from_name: 'Alpha Services',
          Nome: formData.nome,
          Telefone: formData.telefone,
          Email: formData.email,
          Rua: formData.rua,
          Localidade: formData.localidade,
          Distrito: formData.distrito,
          Codigo_Postal: formData.codigoPostal,
          Quantidade: formData.quantidade,
          Equipamento_Encastre: formData.isEncastre ? 'Sim' : 'Não',
          Remocao_Descarte: formData.remocaoDescarte ? 'Sim' : 'Não',
          Valor_Estimado: `${calculateTotal()}€`,
          Observacoes: formData.observacoes || 'Nenhuma'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');

        // Registra a conversão no Google Ads com Enhanced Conversions
        if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
          (window as any).gtag_report_conversion(null, {
            email: formData.email,
            phone: formData.telefone
          });
        }

        setTimeout(() => {
          onClose();
          navigate(`/obrigado/${service.id}`);
          setFormData({
            nome: '',
            telefone: '',
            email: '',
            rua: '',
            localidade: '',
            distrito: '',
            codigoPostal: '',
            observacoes: '',
            quantidade: '1',
            remocaoDescarte: false,
            isEncastre: false,
            rgpdConsent: false
          });
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="bg-blue-600 p-6 text-white relative">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
          <h3 className="text-xl font-bold">Solicitar Instalação</h3>
          <p className="text-blue-100 text-sm mt-1">{service.title}</p>
        </div>

        {/* Body */}
        <div className="p-6 max-h-[85vh] overflow-y-auto">
          {submitStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-2">Pedido Enviado!</h4>
              <p className="text-gray-600">Entraremos em contacto brevemente para confirmar o seu agendamento.</p>
            </div>
          ) : (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Form Side */}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nome Completo</label>
                    <input
                      type="text"
                      required
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                      value={formData.nome}
                      onChange={e => setFormData({ ...formData, nome: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Telefone</label>
                      <input
                        type="tel"
                        required
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                        value={formData.telefone}
                        onChange={e => setFormData({ ...formData, telefone: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</label>
                      <input
                        type="email"
                        required
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Rua / Morada</label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: Rua Direita, 123"
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm mb-3"
                      value={formData.rua}
                      onChange={e => setFormData({ ...formData, rua: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Localidade</label>
                        <input
                          type="text"
                          required
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                          value={formData.localidade}
                          onChange={e => setFormData({ ...formData, localidade: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Distrito</label>
                        <input
                          type="text"
                          required
                          className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                          value={formData.distrito}
                          onChange={e => setFormData({ ...formData, distrito: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Código Postal</label>
                      <input
                        type="text"
                        required
                        placeholder="0000-000"
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                        value={formData.codigoPostal}
                        onChange={e => setFormData({ ...formData, codigoPostal: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t border-gray-100">
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Observações / Notas Extras</label>
                    <textarea
                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm h-20 resize-none"
                      placeholder="Ex: Acesso difícil, prédio sem elevador, etc..."
                      value={formData.observacoes}
                      onChange={e => setFormData({ ...formData, observacoes: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-start gap-3 pt-4 border-t border-gray-100">
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      required
                      id="rgpd-modal"
                      className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-gray-300 checked:bg-blue-600 checked:border-blue-600 transition-all"
                      checked={formData.rgpdConsent}
                      onChange={e => setFormData({ ...formData, rgpdConsent: e.target.checked })}
                    />
                    <i className="ri-check-line absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-[10px] opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"></i>
                  </div>
                  <label htmlFor="rgpd-modal" className="text-[10px] text-gray-500 leading-tight">
                    Concordo com o armazenamento dos meus dados para ser contactado pela Alpha Services. Cumprimos o RGPD.
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg shadow-blue-200 disabled:opacity-50"
                >
                  {isSubmitting ? 'A Enviar...' : 'Confirmar Pedido'}
                </button>
              </form>

              {/* Info Side */}
              <div className="space-y-6">
                {/* O que inclui */}
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <i className="ri-information-fill text-blue-600"></i>
                    O que inclui a Instalação Standard?
                  </h4>
                  <ul className="space-y-2">
                    {[
                      'Desembalagem e inspeção do equipamento',
                      'Remoção das proteções de transporte',
                      'Ligação às redes de água e esgoto existentes',
                      'Ligação à rede elétrica preparada',
                      'Nivelamento e estabilização',
                      'Teste de funcionamento e verificação de fugas',
                      'Breve explicação de funcionamento'
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-600">
                        <i className="ri-check-line text-green-500 font-bold"></i>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Opções de Serviço */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Quantidade</label>
                    <select 
                      className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-1 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.quantidade}
                      onChange={e => setFormData({ ...formData, quantidade: e.target.value })}
                    >
                      <option value="1">1 unidade</option>
                      <option value="2">2 unidades</option>
                      <option value="3">3 unidades</option>
                      <option value="4">4+ unidades</option>
                    </select>
                  </div>

                  {service.hasEncastreOption && (
                    <label className="flex items-start gap-3 cursor-pointer group">
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 checked:bg-blue-600 checked:border-blue-600 transition-all"
                          checked={formData.isEncastre}
                          onChange={e => setFormData({ ...formData, isEncastre: e.target.checked })}
                        />
                        <i className="ri-check-line absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"></i>
                      </div>
                      <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                        Equipamento de encastre (+30,00€)
                      </span>
                    </label>
                  )}

                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="relative flex items-center">
                      <input
                        type="checkbox"
                        className="peer h-5 w-5 cursor-pointer appearance-none rounded border border-gray-300 checked:bg-blue-600 checked:border-blue-600 transition-all"
                        checked={formData.remocaoDescarte}
                        onChange={e => setFormData({ ...formData, remocaoDescarte: e.target.checked })}
                      />
                      <i className="ri-check-line absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 pointer-events-none transition-opacity"></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-gray-900 transition-colors">
                      Remoção e descarte do equipamento antigo
                    </span>
                  </label>
                </div>

                {/* Aviso Importante */}
                <div className="bg-amber-50 rounded-2xl p-5 border border-amber-100">
                  <h4 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2">
                    <i className="ri-error-warning-fill text-amber-600"></i>
                    Aviso Importante
                  </h4>
                  <p className="text-[11px] text-amber-800 leading-relaxed mb-3">
                    O local deve estar <strong>totalmente adequado</strong> para a instalação. Caso contrário, serão cobrados valores extras:
                  </p>
                  <ul className="space-y-1 text-[10px] text-amber-800/80 italic">
                    <li>• Necessidade de cortes em pedra ou bancada</li>
                    <li>• Inexistência de ligação elétrica ou de água próxima</li>
                    <li>• Necessidade de material extra (cabos, tubos, adaptadores)</li>
                  </ul>
                </div>

                {/* Total */}
                <div className="bg-blue-600 p-5 rounded-2xl text-white shadow-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium opacity-90">Total Estimado:</span>
                    <span className="text-2xl font-black">{calculateTotal()}€ <span className="text-xs font-medium opacity-80">+ IVA</span></span>
                  </div>
                  <p className="text-[10px] text-blue-100 leading-tight">
                    * Pagamento de 50% para garantir a data. Se a instalação não for possível por falta de condições no local, o valor cobre a deslocação.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplianceBookingModal;
